import React, { useEffect } from 'react';
import { getCLS, getFID, getFCP, getLCP, getTTFB } from 'web-vitals';

const PerformanceMonitor = () => {
  useEffect(() => {
    // Monitor Core Web Vitals
    const sendToAnalytics = ({ name, delta, id }) => {
      // Send to Google Analytics if available
      if (window.gtag) {
        window.gtag('event', name, {
          event_category: 'Web Vitals',
          event_label: id,
          value: Math.round(name === 'CLS' ? delta * 1000 : delta),
          non_interaction: true,
        });
      }

      // Log to console in development
      if (process.env.NODE_ENV === 'development') {
        console.log(`Web Vital: ${name}`, {
          value: Math.round(name === 'CLS' ? delta * 1000 : delta),
          id,
        });
      }

      // Send to custom analytics endpoint if needed
      if (process.env.REACT_APP_ANALYTICS_ENDPOINT) {
        fetch(process.env.REACT_APP_ANALYTICS_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name,
            value: Math.round(name === 'CLS' ? delta * 1000 : delta),
            id,
            timestamp: Date.now(),
          }),
        }).catch(console.error);
      }
    };

    // Initialize Core Web Vitals monitoring
    getCLS(sendToAnalytics);
    getFID(sendToAnalytics);
    getFCP(sendToAnalytics);
    getLCP(sendToAnalytics);
    getTTFB(sendToAnalytics);

    // Monitor page load performance
    const measurePageLoad = () => {
      if ('performance' in window) {
        const navigation = performance.getEntriesByType('navigation')[0];
        const paint = performance.getEntriesByType('paint');
        
        const metrics = {
          dns: navigation.domainLookupEnd - navigation.domainLookupStart,
          tcp: navigation.connectEnd - navigation.connectStart,
          ttfb: navigation.responseStart - navigation.requestStart,
          domContentLoaded: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
          loadComplete: navigation.loadEventEnd - navigation.loadEventStart,
          firstPaint: paint.find(entry => entry.name === 'first-paint')?.startTime,
          firstContentfulPaint: paint.find(entry => entry.name === 'first-contentful-paint')?.startTime,
        };

        if (process.env.NODE_ENV === 'development') {
          console.log('Page Load Metrics:', metrics);
        }

        // Send to analytics
        if (window.gtag) {
          Object.entries(metrics).forEach(([key, value]) => {
            if (value !== undefined) {
              window.gtag('event', 'timing_complete', {
                name: key,
                value: Math.round(value),
                event_category: 'Page Load',
              });
            }
          });
        }
      }
    };

    // Measure performance after page load
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', measurePageLoad);
    } else {
      measurePageLoad();
    }

    // Monitor resource loading performance
    const observer = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        if (entry.entryType === 'resource') {
          const resourceMetrics = {
            name: entry.name,
            duration: entry.duration,
            transferSize: entry.transferSize,
            decodedBodySize: entry.decodedBodySize,
            initiatorType: entry.initiatorType,
          };

          if (process.env.NODE_ENV === 'development') {
            console.log('Resource Load:', resourceMetrics);
          }

          // Track slow resources
          if (entry.duration > 3000) {
            console.warn('Slow resource detected:', entry.name, `${Math.round(entry.duration)}ms`);
          }
        }
      });
    });

    observer.observe({ entryTypes: ['resource'] });

    // Monitor memory usage (if available)
    if ('memory' in performance) {
      const logMemoryUsage = () => {
        const memory = performance.memory;
        const memoryMetrics = {
          usedJSHeapSize: Math.round(memory.usedJSHeapSize / 1024 / 1024), // MB
          totalJSHeapSize: Math.round(memory.totalJSHeapSize / 1024 / 1024), // MB
          jsHeapSizeLimit: Math.round(memory.jsHeapSizeLimit / 1024 / 1024), // MB
        };

        if (process.env.NODE_ENV === 'development') {
          console.log('Memory Usage:', memoryMetrics);
        }

        // Alert if memory usage is high
        if (memory.usedJSHeapSize > memory.jsHeapSizeLimit * 0.8) {
          console.warn('High memory usage detected:', memoryMetrics);
        }
      };

      // Log memory usage every 30 seconds
      setInterval(logMemoryUsage, 30000);
      logMemoryUsage(); // Initial log
    }

    // Cleanup
    return () => {
      observer.disconnect();
    };
  }, []);

  // This component doesn't render anything
  return null;
};

export default PerformanceMonitor;
